<template>
  <div class="chat-turn">
    <prompt-block :block="questionBlock" />
    <stage-trail :turn="turn" />
    <notice-block v-if="substituteBlock" :block="substituteBlock" />
    <template v-else-if="!isFailed">
      <template v-for="(part, index) in turn.parts" :key="index">
        <block-renderer
          v-if="part.kind === 'text'"
          :block="toMarkdownBlock(part, index)"
        />
        <notice-block
          v-else-if="part.kind === 'notice'"
          :block="toNoticeBlock(part, index)"
        />
        <block-renderer
          v-else-if="part.kind === 'widget'"
          :block="part.block"
          @download="emit('download', $event)"
        />
      </template>
    </template>
    <el-alert
      v-if="turn.error && turn.status !== 'done'"
      :type="turn.status === 'aborted' ? 'info' : 'error'"
      :closable="false"
      show-icon
      class="chat-turn-error"
    >
      <template #title>
        <span data-testid="ai-chat-turn-error-title">
          {{ t(`aichat.main.errors.${errorMessage.title}`) }}
        </span>
      </template>
      <span v-if="errorMessage.text" data-testid="ai-chat-turn-error-text">
        {{ t(`aichat.main.errors.${errorMessage.text}`) }}
      </span>
      <el-button
        v-if="turn.error.retryable"
        link
        type="primary"
        size="small"
        class="resend-button"
        data-testid="ai-chat-turn-resend-button"
        @click="emit('resend', turn)"
      >
        {{ t('aichat.main.errors.resend') }}
      </el-button>
    </el-alert>
    <hint-chips-block
      v-if="turn.status === 'done' && turn.chips.length"
      :block="chipsBlock"
      :disabled="disabled"
      @followup="emit('followup', $event)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import BlockRenderer from './blocks/block-renderer.vue'
import HintChipsBlock from './blocks/hint-chips.vue'
import NoticeBlock from './blocks/notice.vue'
import PromptBlock from './blocks/prompt.vue'
import StageTrail from './stage-trail.vue'
import {
  ERROR_MESSAGES,
  NOTICE_MESSAGES,
  UNKNOWN_ERROR_MESSAGE
} from '../constants/error-messages'
import type {
  CardBlock,
  HintChipsBlock as HintChipsBlockData,
  MarkdownBlock,
  NoticeBlock as NoticeBlockData,
  NoticePart,
  PromptBlock as PromptBlockData,
  TextPart,
  Turn
} from '../types'

/* one question and its streamed answer: stages, text, widgets and notices, the error with a resend, the follow-up chips */
const props = withDefaults(
  defineProps<{
    turn: Turn
    /* true while another turn streams, so the chips cannot start a second one */
    disabled?: boolean
  }>(),
  { disabled: false }
)

const emit = defineEmits<{
  followup: [prompt: string]
  resend: [turn: Turn]
  download: [block: CardBlock]
}>()

const { t } = useI18n()

/* a failed turn shows its error alone, never the partial answer streamed before it */
const isFailed = computed(() => props.turn.status === 'failed')

const errorMessage = computed(
  () =>
    (props.turn.error && ERROR_MESSAGES[props.turn.error.code]) ||
    UNKNOWN_ERROR_MESSAGE
)

const questionBlock = computed<PromptBlockData>(() => ({
  id: `${props.turn.localId}-prompt`,
  type: 'prompt',
  text: props.turn.prompt
}))

const substituteBlock = computed<NoticeBlockData | null>(() =>
  props.turn.substitute
    ? {
        id: `${props.turn.localId}-substitute`,
        type: 'notice',
        level: 'warning',
        code: 'substitute_safe_response',
        title: t('aichat.main.errors.substitute'),
        text: props.turn.text
      }
    : null
)

const chipsBlock = computed<HintChipsBlockData>(() => ({
  id: `${props.turn.localId}-chips`,
  type: 'hint_chips',
  chips: props.turn.chips
}))

const toMarkdownBlock = (part: TextPart, index: number): MarkdownBlock => ({
  id: `${props.turn.localId}-text-${index}`,
  type: 'markdown',
  text: part.text
})

const toNoticeBlock = (part: NoticePart, index: number): NoticeBlockData => {
  const { scope } = part.error
  const key =
    scope && scope !== 'turn'
      ? NOTICE_MESSAGES[scope]
      : NOTICE_MESSAGES.tool_call
  return {
    id: part.error.error_id || `${props.turn.localId}-notice-${index}`,
    type: 'notice',
    level: 'warning',
    code: part.error.code,
    text: t(`aichat.main.errors.${key}`)
  }
}
</script>

<style scoped>
.chat-turn {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.chat-turn-error {
  align-self: flex-start;
  max-width: 80%;
}

.resend-button {
  margin-left: 0.75rem;
}
</style>
